import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, CssBaseline, Divider, FormControl, FormLabel, TextField, Typography, Stack, Avatar, IconButton } from '@mui/material';
import Base from '../../Base/views/Base';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import Swal from "sweetalert2";

import axiosInstance from '../../../axiosInstance';

const Card = styled(MuiCard)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    boxShadow: 'hsla(220, 30%, 5%, 0.05) 0px 5px 15px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px'
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: { padding: theme.spacing(4) },
    '&::before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        zIndex: -1,
        inset: 0,
        backgroundImage: 'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))'
    }
}));

export default function EditProfile() {
    const navigate = useNavigate()
    const [data, setData] = useState({
        email: '',
        hotel: {
            id: '',
            name: '',
            location: '',
            description: '',
            logo: '',
            address: '',
            contact: '',
            gst_number: '',
            pan_number: ''
        }
    });
    const [avatar, setAvatar] = useState(null);
    const [loading, setLoading] = useState(true);

    const getData = () => {
        axiosInstance(`/api/profile/`, {
            method: "GET"
        }).then(res => {
            const user = res?.data;
            setData({
                email: user?.email || '',
                hotel: {
                    id: user?.hotel_detail?.id || '',
                    name: user?.hotel_detail?.name || '',
                    location: user?.hotel_detail?.location || '',
                    description: user?.hotel_detail?.description || '',
                    address: user?.hotel_detail?.address,
                    contact: user?.hotel_detail?.contact,
                    gst_number: user?.hotel_detail?.gst_number,
                    pan_number: user?.hotel_detail?.pan_number
                }
            });
            setAvatar(user?.hotel_detail?.logo || '');
            setLoading(false);
        }).catch(err => {
            setLoading(false);
        });
    }

    useEffect(() => {
        getData();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith('hotel.')) {
            const key = name.split('.')[1];
            setData(prev => ({
                ...prev,
                hotel: { ...prev.hotel, [key]: value }
            }));
        } else {
            setData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatar(reader.result);  // base64 string including data:image/png;base64,...
            };
            reader.readAsDataURL(file);
        }
    };


    const handleSubmit = () => {
        axiosInstance(`/api/profile/`, {
            method: "PUT",
            data: {
                email: data.email,
                hotel_id: data.hotel.id,
                hotel: {
                    name: data.hotel.name,
                    location: data.hotel.location,
                    description: data.hotel.description,
                    logo: avatar,
                    address: data.hotel.address,
                    contact: data.hotel.contact,
                    gst_number: data.hotel.gst_number,
                    pan_number: data.hotel.pan_number
                }
            }
        })
            .then(() => {
                Swal.fire("Success", "Profile updated successfully", "success");
                navigate("/");  // Navigate to the desired page after successful update
            })
            .catch(err => {
                console.error(err.response?.data);  // Log the error response to understand what went wrong
                window.alert("Failed to update profile.");
            });
    };

    if (loading) return <Typography>Loading...</Typography>;

    return (
        <Base>
            <CssBaseline enableColorScheme />
            <SignInContainer direction="column">
                <Card variant="outlined">
                    {/* Avatar Upload Top Right */}
                    <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
                        <IconButton color="primary" component="label">
                            <input hidden accept="image/*" type="file" onChange={handleAvatarChange} />
                            <Avatar
                                src={avatar} // fallback image
                                sx={{ width: 60, height: 60 }}
                            />

                        </IconButton>
                    </Box>

                    <Typography variant="h4">Edit Your Profile</Typography>

                    <Box component="form" noValidate sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ flex: '1 1 48%' }}>
                            <FormControl fullWidth>
                                <FormLabel>Email *</FormLabel>
                                <TextField
                                    name="email"
                                    type="email"
                                    value={data.email}
                                    required
                                    onChange={handleChange}
                                    size="small"
                                />
                            </FormControl>
                        </Box>

                        <Box sx={{ flex: '1 1 48%' }}>
                            <FormControl fullWidth>
                                <FormLabel>Hotel Name *</FormLabel>
                                <TextField
                                    name="hotel.name"
                                    value={data.hotel.name}
                                    required
                                    onChange={handleChange}
                                    size="small"
                                />
                            </FormControl>
                        </Box>

                        <Box sx={{ flex: '1 1 48%' }}>
                            <FormControl fullWidth>
                                <FormLabel>Hotel Location *</FormLabel>
                                <TextField
                                    name="hotel.location"
                                    value={data.hotel.location}
                                    required
                                    onChange={handleChange}
                                    size="small"
                                />
                            </FormControl>
                        </Box>

                        <Box sx={{ flex: '1 1 100%' }}>
                            <FormControl fullWidth>
                                <FormLabel>Hotel Description</FormLabel>
                                <TextField
                                    name="hotel.description"
                                    value={data.hotel.description}
                                    multiline
                                    rows={3}
                                    onChange={handleChange}
                                    size="small"
                                />
                            </FormControl>
                        </Box>
                        <Box sx={{ flex: '1 1 48%' }}>
                            <FormControl fullWidth>
                                <FormLabel>Address</FormLabel>
                                <TextField
                                    name="hotel.address"
                                    value={data.hotel.address}
                                    multiline
                                    rows={2}
                                    onChange={handleChange}
                                    size="small"
                                />
                            </FormControl>
                        </Box>
                        <Box sx={{ flex: '1 1 48%' }}>

                            <FormControl fullWidth>
                                <FormLabel>Contact</FormLabel>
                                <TextField
                                    name="hotel.contact"
                                    value={data.hotel.contact}
                                    multiline
                                    rows={2}
                                    onChange={handleChange}
                                    size="small"
                                />
                            </FormControl>
                        </Box>
                        <Box sx={{ flex: '1 1 48%' }}>
                            <FormControl fullWidth>
                                <FormLabel>GST Number</FormLabel>
                                <TextField
                                    name="hotel.gst_number"
                                    value={data.hotel.gst_number}
                                    onChange={handleChange}
                                    size="small"
                                    inputProps={{
                                        pattern: '^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$',
                                        title: 'Enter valid GST number (e.g., 22ABCDE1234F1Z5)'
                                    }}
                                />
                            </FormControl>
                        </Box>

                        <Box sx={{ flex: '1 1 48%' }}>
                            <FormControl fullWidth>
                                <FormLabel>PAN Number</FormLabel>
                                <TextField
                                    name="hotel.pan_number"
                                    value={data.hotel.pan_number}
                                    onChange={handleChange}
                                    size="small"
                                    inputProps={{
                                        pattern: '[A-Z]{5}[0-9]{4}[A-Z]{1}',
                                        title: 'Enter valid PAN number (e.g., ABCDE1234F)'
                                    }}
                                />
                            </FormControl>
                        </Box>


                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button variant="contained" onClick={handleSubmit}>
                            Save
                        </Button>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" sx={{ textAlign: 'center', color: 'gray' }}>
                        Your changes will be saved securely.
                    </Typography>
                </Card>
            </SignInContainer>
        </Base>
    );
}
